/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { Callout, type PageAction } from "@octopusdeploy/design-system-components";
import type { AccountResource, ActionProperties, ActionTemplateParameterResource, ActionTemplateResource, CommunityActionTemplateResource, EnvironmentV2Resource, FeedResource, GitCredentialResource, GitDependencyReference, GitHubAppConnectionSummary, PackageReference, StepPackageInputs, } from "@octopusdeploy/octopus-server-client";
import { ControlType, getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled, Permission, Repository } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { cloneDeep, isEqual } from "lodash";
import * as React from "react";
import { ProcessAccountsContextProvider } from "~/areas/projects/components/Process/Contexts/ProcessAccountsContextProvider";
import { ProcessFeedsContextProvider } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import { ProcessGitCredentialsContextProvider } from "~/areas/projects/components/Process/Contexts/ProcessGitCredentialsContextProvider";
import { ProcessGitHubConnectionsContextProvider } from "~/areas/projects/components/Process/Contexts/ProcessGitHubConnectionsContextProvider";
import { useGitHubConnectionRepositorySourcingEnabled } from "~/areas/projects/components/Process/useGitHubConnectionRepositorySourcingEnabled";
import { repository } from "~/clientInstance";
import { createActionTemplateInitialInputsBuilder } from "~/components/ActionInputs/ActionTemplateActionInputs";
import { ActionTemplatePropertiesEditorAndFeatures } from "~/components/ActionPropertiesEditor/ActionTemplatePropertiesEditorAndFeatures";
import ActionTemplateParameterList from "~/components/ActionTemplateParametersList";
import { default as pluginRegistry } from "~/components/Actions/pluginRegistry";
import type { ActionPlugin } from "~/components/Actions/pluginRegistry";
import { ScriptActionContext } from "~/components/Actions/script/ScriptActionContext";
import DialogOpener from "~/components/Dialog/DialogOpener";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import type { OptionalFormBaseComponentState } from "~/components/FormBaseComponent/FormBaseComponent";
import { OptionalFormBaseComponent } from "~/components/FormBaseComponent/FormBaseComponent";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import Logo from "~/components/Logo/Logo";
import Markdown from "~/components/Markdown";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import InternalRedirect from "~/components/Navigation/InternalRedirect/InternalRedirect";
import OpenFeatureDialog from "~/components/OpenFeatureDialog/OpenFeatureDialog";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import type { ConvertedOverflowMenuItems } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import PermissionCheck, { hasPermission } from "~/components/PermissionCheck/PermissionCheck";
import { Section } from "~/components/Section/Section";
import { ExpandableFormSection, ExpansionButtons, required, Summary, Text } from "~/components/form";
import { default as LogoEditor } from "~/components/form/LogoEditor/LogoEditor";
import type { LogoEditorSettings } from "~/components/form/LogoEditor/LogoEditor";
import MarkdownEditor from "~/components/form/MarkdownEditor/MarkdownEditor";
import type { SummaryNode } from "~/components/form/Sections/ExpandableFormSection";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import ExportActionTemplateDialog from "../ExportActionTemplateDialog";
import SaveAsDialog from "../SaveAsDialog";
import type { CreateStepTemplatePageProps } from "./CreateStepTemplatePage";
import type { StepTemplateDetailsPageProps } from "./StepTemplateDetailsPage";
export interface StepTemplatePageQueryParams {
    activeTab: "step" | "settings" | "parameters";
}
type StepTemplatePageProps = CreateStepTemplatePageProps | StepTemplateDetailsPageProps;
interface StepTemplateModel {
    template: Partial<ActionTemplateResource>;
    logo: LogoEditorSettings;
}
export interface StepTemplatePageState extends OptionalFormBaseComponentState<StepTemplateModel> {
    redirectTo: LinkHref;
    plugin: ActionPlugin;
    isLoaded: boolean;
    isNew: boolean;
    showSaveAs: boolean;
    localNames: string[];
    communityTemplate?: CommunityActionTemplateResource;
    feeds: FeedResource[];
    accounts: AccountResource[];
    environments: EnvironmentV2Resource[];
    gitCredentials: GitCredentialResource[];
    gitHubConnections: GitHubAppConnectionSummary[];
    packagesInitialized: boolean;
    gitDependenciesInitialized: boolean;
    propertiesInitialized: boolean;
}
function isActionTemplateFromStepPackage(model: StepTemplateModel | undefined) {
    return model?.template.Inputs !== undefined && model?.template.StepPackageVersion !== undefined;
}
function isAddNewStepTemplatePageProps(props: CreateStepTemplatePageProps | StepTemplateDetailsPageProps): props is Readonly<CreateStepTemplatePageProps> {
    return "actionType" in props;
}
export function StepTemplatePage(props: StepTemplatePageProps) {
    const gitHubConnectionRepositorySourcingEnabled = useGitHubConnectionRepositorySourcingEnabled();
    return <StepTemplatePageInternal {...props} gitHubConnectionRepositorySourcingEnabled={gitHubConnectionRepositorySourcingEnabled}/>;
}
type StepTemplatePageInternalProps = StepTemplatePageProps & {
    gitHubConnectionRepositorySourcingEnabled: boolean;
};
class StepTemplatePageInternal extends OptionalFormBaseComponent<StepTemplatePageInternalProps, StepTemplatePageState, StepTemplateModel> {
    constructor(props: StepTemplatePageInternalProps) {
        super(props);
        this.state = {
            redirectTo: null!,
            plugin: null!,
            isLoaded: false,
            isNew: isAddNewStepTemplatePageProps(this.props),
            showSaveAs: false,
            localNames: [],
            feeds: [],
            accounts: [],
            environments: [],
            gitCredentials: [],
            gitHubConnections: [],
            packagesInitialized: false,
            gitDependenciesInitialized: false,
            propertiesInitialized: false,
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            let model: StepTemplateModel | undefined = undefined;
            let plugin: ActionPlugin | undefined = undefined;
            const feeds = await this.loadFeeds();
            if (isAddNewStepTemplatePageProps(this.props)) {
                plugin = await pluginRegistry.getAction(this.props.actionType);
                model = this.getNewTemplate(this.props.actionType, plugin, feeds);
            }
            else {
                model = await this.getExistingTemplate(this.props.templateId);
                // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                plugin = await pluginRegistry.getAction(model?.template.ActionType!, model?.template.StepPackageVersion);
            }
            const communityTemplate = model.template.CommunityActionTemplateId ? repository.CommunityActionTemplates.get(model.template.CommunityActionTemplateId) : Promise.resolve(null!);
            const accounts = this.loadAccounts();
            const environments = this.loadEnvironments();
            const gitCredentials = this.loadGitCredentials();
            const gitHubConnections = this.loadGitHubConnections();
            this.setState({
                model,
                cleanModel: cloneDeep(model),
                communityTemplate: await communityTemplate,
                plugin,
                isLoaded: true,
                isNew: isAddNewStepTemplatePageProps(this.props),
                localNames: this.toLocalNames(model.template.Parameters!),
                feeds: feeds,
                accounts: await accounts,
                environments: await environments,
                gitCredentials: await gitCredentials,
                gitHubConnections: await gitHubConnections,
            });
        }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    loadFeeds = () => {
        return hasPermission(Permission.FeedView) ? repository.Feeds.all() : Promise.resolve<FeedResource[]>([]);
    };
    loadVariables = () => {
        return repository.Variables.getSpecialVariableNames();
    };
    refreshFeeds = () => {
        return this.doBusyTask(async () => {
            const feeds = await this.loadFeeds();
            this.setState({ feeds });
        });
    };
    refreshGitCredentials = () => {
        return this.doBusyTask(async () => {
            const gitCredentials = await this.loadGitCredentials();
            this.setState({ gitCredentials });
        });
    };
    loadGitCredentials = async () => (hasPermission(Permission.GitCredentialView) ? (await repository.GitCredentials.list({ take: Repository.takeAll })).Items : []);
    refreshGitHubConnections = async () => {
        this.doBusyTask(async () => {
            const gitHubConnections = await this.loadGitHubConnections();
            this.setState({ gitHubConnections });
        });
    };
    loadGitHubConnections = async () => (this.props.gitHubConnectionRepositorySourcingEnabled && hasPermission(Permission.GitCredentialView) ? (await repository.GitHubApp.getConnections(0, Repository.takeAll)).Connections : []);
    loadAccounts = () => (hasPermission(Permission.AccountView) ? repository.Accounts.all() : Promise.resolve<AccountResource[]>([]));
    loadEnvironments = async () => {
        const ephemeralEnvironmentsAreEnabled = isFeatureToggleEnabled("ephemeral-environments");
        return hasPermission(Permission.EnvironmentView) ? await getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, repository) : [];
    };
    refreshAccounts = () => this.doBusyTask(async () => {
        const accounts = await this.loadAccounts();
        this.setState({ accounts });
    });
    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo} push={true}/>;
        }
        const excludedControlTypes: ControlType[] = isActionTemplateFromStepPackage(this.state.model)
            ? [
                ControlType.AmazonWebServicesAccount,
                ControlType.AzureAccount,
                ControlType.Certificate,
                ControlType.GoogleCloudAccount,
                ControlType.Package,
                ControlType.WorkerPool,
                ControlType.UsernamePasswordAccount,
                ControlType.Feed,
                ControlType.TargetTags,
                ControlType.Environments,
                ControlType.Teams,
                ControlType.TenantTags,
                ControlType.Channels,
                ControlType.Project,
            ]
            : [ControlType.WorkerPool, ControlType.Feed, ControlType.TargetTags, ControlType.Environments, ControlType.Teams, ControlType.TenantTags, ControlType.Channels, ControlType.Project];
        const overflowActions = this.getOverflowActions();
        return (<LegacyForm model={this.state.model} cleanModel={this.state.cleanModel} savePermission={{ permission: this.state.isNew ? Permission.ActionTemplateCreate : Permission.ActionTemplateEdit }} onSaveClick={this.state.isLoaded && this.isBasedOnCommunityTemplate() ? this.handleSaveAsCopyClick : this.handleSaveClick} saveText={this.state.isLoaded ? this.saveText() : undefined} disableDirtyFormChecking={this.state.isLoaded && this.isBasedOnCommunityTemplate() && !this.dataChanged()}>
                {({ FormContent, createSaveAction }) => (<ScriptActionContext.Provider value={{ loadVariables: this.loadVariables }}>
                        <ProcessFeedsContextProvider feeds={this.state.feeds} refreshFeeds={this.refreshFeeds}>
                            <ProcessAccountsContextProvider accounts={this.state.accounts} refreshAccounts={this.refreshAccounts}>
                                <ProcessGitCredentialsContextProvider gitCredentials={this.state.gitCredentials} refreshGitCredentials={this.refreshGitCredentials}>
                                    <ProcessGitHubConnectionsContextProvider gitHubConnections={this.state.gitHubConnections} refreshGitHubConnections={this.refreshGitHubConnections}>
                                        <PageContent header={{
                    primaryAction: createSaveAction({ saveButtonLabel: this.state.isLoaded ? this.saveButtonLabel() : undefined }),
                    title: this.state.isNew ? "Create step template" : "Details",
                    breadcrumbs: this.state.isNew ? [{ label: "Step Templates", pageUrl: links.stepTemplatesPage.generateUrl({ spaceId: this.props.spaceId }) }] : undefined,
                    overflowActions: overflowActions.menuItems,
                    pageActions: this.state.isLoaded ? [this.getOpenFeaturesPageAction()].filter((x): x is PageAction => !!x) : undefined,
                }} busy={this.state.busy} errors={this.errors} callout={this.isUpdateAvailable()
                    ? {
                        type: "warning",
                        title: "New version available",
                        content: (<>
                                                                  View the new version of the{" "}
                                                                  <InternalLink to={links.communityStepTemplatePage.generateUrl({ spaceId: this.props.spaceId, templateId: this.state.communityTemplate!.Id })}>
                                                                      {this.state.model!.template.Name}
                                                                  </InternalLink>{" "}
                                                                  step template.
                                                              </>),
                    }
                    : undefined} currentTab={this.props.queryParams.activeTab} onTabChanged={(value) => this.props.setQueryParams((prev) => ({ ...prev, activeTab: value }))} tabs={this.state.isLoaded
                    ? [
                        {
                            value: "step",
                            label: "Step",
                            content: (<FormContent hideExpandAll={true}>
                                                                      <ExpansionButtons errors={this.errors?.fieldErrors} expandAllOnMount={this.state.isNew}/>
                                                                      <ActionTemplatePropertiesEditorAndFeatures properties={this.state.model!.template.Properties!} packages={this.state.model!.template.Packages!} gitDependencies={this.state.model!.template.GitDependencies} environments={this.state.environments} isNew={this.state.isNew} doBusyTask={this.doBusyTask} busy={this.state.busy!} plugin={this.state.plugin} inputs={this.state.model!.template.Inputs!} setInputs={this.setInputs} setProperties={this.setProperties} setPackages={this.setPackages} setGitDependencies={this.setGitDependencies} getFieldError={this.getFieldError} localNames={this.state.localNames} errors={this.errors?.fieldErrors} parameters={this.state.model!.template.Parameters ?? []} hasFeatures={this.hasFeatures()} enabledFeatures={(this.state.model!.template.Properties!["Octopus.Action.EnabledFeatures"] as string) || ""} openFeaturesElement={this.state.isLoaded && this.addFeaturesElement()}/>
                                                                  </FormContent>),
                        },
                        {
                            value: "parameters",
                            label: "Parameters",
                            content: (<FormContent hideExpandAll={true}>
                                                                      <Section>Parameters define variables that are set when the step template is included in a project's deployment process.</Section>
                                                                      <Section>
                                                                          <ActionTemplateParameterList parameters={this.state.model!.template.Parameters} name="parameter" excludedControlTypes={excludedControlTypes} editPermission={{
                                    permission: Permission.ActionTemplateEdit,
                                    project: "*",
                                    environment: "*",
                                }} onParametersChanged={this.handleParametersChanged}/>
                                                                      </Section>
                                                                  </FormContent>),
                        },
                        {
                            value: "settings",
                            label: "Settings",
                            content: (<FormContent hideExpandAll={true}>
                                                                      <ExpansionButtons containerKey="settings" errors={this.errors?.fieldErrors} expandAllOnMount={this.state.isNew}/>
                                                                      <ExpandableFormSection containerKey="settings" errorKey="Name" title="Name" focusOnExpandAll summary={this.state.model!.template.Name ? Summary.summary(this.state.model!.template.Name) : Summary.placeholder("Please enter a name for your step template")} help="A short, memorable, unique name for this step template. Example: Success Notification.">
                                                                          <Text value={this.state.model!.template.Name!} onChange={(name) => this.setChildState2("model", "template", { Name: name })} label="Name" validate={required("Please enter a step template name")}/>
                                                                      </ExpandableFormSection>
                                                                      <PermissionCheck permission={Permission.ActionTemplateEdit}>
                                                                          <ExpandableFormSection containerKey="settings" errorKey="logo" title="Logo" summary={this.logoSummary()} help={this.isBasedOnCommunityTemplate() ? "The logo of a community step template is read only." : "Choose an image to use as a logo"}>
                                                                              {this.isBasedOnCommunityTemplate() ? (<div>
                                                                                      <Logo url={this.state.model!.template.Links!.Logo}/>
                                                                                      If you want to update it then you have to copy the template first.
                                                                                  </div>) : (<LogoEditor value={this.state.model!.logo} onChange={(logo) => this.setModelState({ logo })}/>)}
                                                                          </ExpandableFormSection>
                                                                      </PermissionCheck>
                                                                      <ExpandableFormSection containerKey="settings" errorKey="Description" title="Description" summary={this.descriptionSummary()} help="This summary will be presented to users when selecting the step template for inclusion in a project.">
                                                                          <MarkdownEditor value={this.state.model!.template.Description} onChange={(description) => this.setChildState2("model", "template", { Description: description })} label="Description"/>
                                                                      </ExpandableFormSection>
                                                                  </FormContent>),
                        },
                    ]
                    : []}/>
                                        {overflowActions.dialogs}
                                        {this.state.isLoaded && (<DialogOpener open={this.state.showSaveAs} onClose={() => this.setState({ showSaveAs: false })}>
                                                <SaveAsDialog originalName={this.state.model!.template.Name!} onSave={this.onSaveAsCopy}/>
                                            </DialogOpener>)}
                                    </ProcessGitHubConnectionsContextProvider>
                                </ProcessGitCredentialsContextProvider>
                            </ProcessAccountsContextProvider>
                        </ProcessFeedsContextProvider>
                    </ScriptActionContext.Provider>)}
            </LegacyForm>);
    }
    private handleParametersChanged = (parameters: ActionTemplateParameterResource[]) => {
        this.setState((state) => ({
            model: {
                ...state.model,
                template: {
                    ...state.model!.template,
                    Parameters: parameters,
                },
            },
            localNames: this.toLocalNames(parameters),
        }));
    };
    private setInputs = (inputs: StepPackageInputs) => {
        this.setState((prev) => ({
            model: {
                ...prev.model,
                template: {
                    ...prev.model!.template,
                    Inputs: inputs,
                },
            },
        }));
    };
    private setProperties = (properties: ActionProperties, initialise?: boolean) => {
        this.setState((prev) => ({
            model: {
                ...prev.model,
                template: {
                    ...prev.model!.template,
                    Properties: {
                        ...prev.model!.template.Properties,
                        ...properties,
                    },
                },
            },
        }), () => {
            if (initialise && !this.state.propertiesInitialized) {
                this.setState((prev) => ({
                    propertiesInitialized: true,
                    cleanModel: {
                        ...prev.cleanModel,
                        template: {
                            ...prev.cleanModel!.template,
                            Properties: {
                                ...prev.cleanModel!.template.Properties,
                                ...properties,
                            },
                        },
                    },
                }));
            }
        });
    };
    private setPackages = (packages: PackageReference[], initialise?: boolean) => {
        this.setState((prev) => ({
            model: {
                ...prev.model,
                template: {
                    ...prev.model!.template,
                    Packages: [...packages],
                },
            },
        }), () => {
            if (initialise && !this.state.packagesInitialized) {
                this.setState((prev) => ({
                    packagesInitialized: true,
                    cleanModel: {
                        ...prev.cleanModel,
                        template: {
                            ...prev.cleanModel!.template,
                            Packages: [...packages],
                        },
                    },
                }));
            }
        });
    };
    private setGitDependencies = (gitDependencies: GitDependencyReference[], initialise?: boolean) => {
        this.setState((prev) => ({
            model: {
                ...prev.model,
                template: {
                    ...prev.model!.template,
                    GitDependencies: [...gitDependencies],
                },
            },
        }), () => {
            if (initialise && !this.state.gitDependenciesInitialized) {
                this.setState((prev) => ({
                    gitDependenciesInitialized: true,
                    cleanModel: {
                        ...prev.cleanModel,
                        template: {
                            ...prev.cleanModel!.template,
                            GitDependencies: [...gitDependencies],
                        },
                    },
                }));
            }
        });
    };
    private logoSummary(): SummaryNode {
        if (!this.state.model!.template.Links || this.state.model!.logo.reset) {
            return Summary.placeholder("Default logo");
        }
        if (this.state.model!.logo.file) {
            return Summary.summary(this.state.model!.logo.file.name);
        }
        return Summary.summary(<Logo url={this.state.model!.template.Links.Logo}/>);
    }
    private descriptionSummary() {
        return this.state.model!.template.Description ? Summary.summary(<Markdown markup={this.state.model!.template.Description}/>) : Summary.placeholder("No step template description provided yet");
    }
    private getNewTemplate(actionType: string, plugin: ActionPlugin, feeds: FeedResource[]): StepTemplateModel {
        const initialInputsBuilder = createActionTemplateInitialInputsBuilder(feeds);
        const initialInputs = plugin.createInitialInputs?.(initialInputsBuilder);
        return {
            template: {
                ActionType: actionType,
                Properties: {},
                Parameters: [],
                Packages: initialInputs ? initialInputs.packages : [],
                Inputs: plugin.getInitialInputsAndPackages?.()?.inputs,
                StepPackageVersion: plugin.version,
            },
            logo: {
                file: null!,
                reset: false,
            },
        };
    }
    private async getExistingTemplate(templateId: string): Promise<StepTemplateModel> {
        const actionTemplate = await repository.ActionTemplates.get(templateId);
        return {
            template: actionTemplate,
            logo: {
                file: null!,
                reset: false,
            },
        };
    }
    private saveText() {
        return this.state.isNew ? "Step template created" : "Step template updated";
    }
    private getOverflowActions(): ConvertedOverflowMenuItems {
        if (!this.state.isLoaded)
            return { menuItems: [], dialogs: [] };
        const exportAction = OverflowMenuItems.dialogItem("Export", <ExportActionTemplateDialog template={this.state.model!.template}/>);
        const legacyOverflowActions = !this.state.isNew && !!this.state.model && !!this.state.model.template
            ? [
                OverflowMenuItems.navItem("Run", links.runStepTemplatePage.generateUrl({ spaceId: this.props.spaceId, templateId: this.state.model.template.Id! })),
                exportAction,
                OverflowMenuItems.deleteItemDefault("step template", this.handleDeleteConfirm, { permission: Permission.ActionTemplateDelete }, undefined, <Callout title={"In-use step templates must not be deleted"} type={"warning"}>
                              {`If you are using Config as Code, please check your deployment and runbook OCL files for steps referring to '${this.state.model.template.Version === 0 ? this.state.model.template.Name : this.state.model.template.Id}' before deleting.`}
                          </Callout>),
                [
                    OverflowMenuItems.navItem("Audit Trail", links.auditPage.generateUrl({ regardingAny: [this.state.model.template.Id!] }), {
                        permission: Permission.EventView,
                        wildcard: true,
                    }),
                ],
            ]
            : [exportAction];
        return OverflowMenuConverterVNext.convertAll(legacyOverflowActions);
    }
    private hasFeatures() {
        return pluginRegistry.hasFeaturesForAction(this.state.plugin.actionType);
    }
    private addFeaturesElement = (): React.ReactElement | undefined => {
        if (!this.hasFeatures()) {
            return undefined;
        }
        return <OpenFeatureDialog actionType={this.state.model!.template.ActionType!} properties={this.state.model!.template.Properties!} saveDone={(features) => this.setProperties({ ["Octopus.Action.EnabledFeatures"]: features })}/>;
    };
    private getOpenFeaturesPageAction = (): PageAction | undefined => {
        const dialog = this.addFeaturesElement();
        if (!dialog) {
            return undefined;
        }
        return { type: "custom", content: dialog, key: "Configure features" };
    };
    private handleSaveClick = async () => {
        await this.doBusyTask(async () => {
            const resource = await this.saveTemplate(this.state.model!.template as ActionTemplateResource, this.state.model!.logo);
            if (!isAddNewStepTemplatePageProps(this.props)) {
                this.props.onActionTemplateUpdated();
            }
            if (this.state.isNew) {
                this.setState({ redirectTo: links.stepTemplateDetailsPage.generateUrl({ spaceId: resource.SpaceId, templateId: resource.Id }) });
            }
        });
    };
    private async saveTemplate(template: ActionTemplateResource, logo: LogoEditorSettings) {
        const savedTemplate = await repository.ActionTemplates.save(template);
        await repository.Logos.saveLogo(savedTemplate, logo.file!, logo.reset);
        // Important: We need to do a GET here so that the new logo url is retrieved
        const actionTemplate = await repository.ActionTemplates.get(savedTemplate.Id);
        const model: StepTemplateModel = { template: actionTemplate, logo: { file: null!, reset: false } };
        this.setState({
            model,
            cleanModel: cloneDeep(model),
        });
        return actionTemplate;
    }
    private handleSaveAsCopyClick = async () => {
        this.setState({ showSaveAs: true });
    };
    private onSaveAsCopy = async (newName: string) => {
        this.setState({ showSaveAs: false });
        await this.doBusyTask(async () => {
            const clone = cloneDeep(this.state.model!.template);
            clone.Id = null!;
            clone.Name = newName;
            const newTemplate = await this.saveTemplate(clone as ActionTemplateResource, this.state.model!.logo);
            this.setState({ redirectTo: links.stepTemplateDetailsPage.generateUrl({ spaceId: newTemplate.SpaceId, templateId: newTemplate.Id }) });
        });
    };
    private handleDeleteConfirm = async () => {
        const result = await repository.ActionTemplates.del(this.state.model!.template as ActionTemplateResource);
        this.setState((state) => {
            return {
                model: null,
                cleanModel: null,
                redirectTo: links.stepTemplatesPage.generateUrl({ spaceId: this.props.spaceId }),
            };
        });
        return true;
    };
    private isBasedOnCommunityTemplate = () => {
        return !!this.state.model!.template.CommunityActionTemplateId;
    };
    private saveButtonLabel = () => {
        return this.isBasedOnCommunityTemplate() ? "Save as copy" : "Save";
    };
    private dataChanged() {
        return !isEqual(this.state.model, this.state.cleanModel);
    }
    private toLocalNames(parameters: ActionTemplateParameterResource[]) {
        return [...parameters].sort((a, b) => a.Name.localeCompare(b.Name)).map((p) => p.Name);
    }
    private isUpdateAvailable() {
        return (this.state.communityTemplate && this.state.model!.template.Version && this.state.model!.template.Version < this.state.communityTemplate.Version)!;
    }
}
